/**
 * # Credit Card Input Set
 * A way to instantiate multiple credit card inputs at once.
 *
 * @example
 * new Spark.Payments.UnifiedCreditCard(el, {...});
 *
 * @module components/payments/unified-credit-card.js
 */

import CardNumber from './card-number';
import CardExpiration from './card-expiration';
import CardSecurityCode from './card-security-code';

import BaseComponent from '../base';

let onValidEvent;
let onInvalidEvent;

class CardSet extends BaseComponent {

  /**
   * CardSet constructor.
   * @param {Element} el
   * @param {Object} params
   */
  constructor(el, params = {}) {
    super(el, params);

    this._bindEventListenerCallbacks();
    this._addEventListeners();

    onValidEvent = document.createEvent('Event');
    onInvalidEvent = document.createEvent('Event');

    onValidEvent.initEvent('valid', true, true);
    onInvalidEvent.initEvent('invalid', true, true);
  }


  /**
   * Sets up references
   * @param {Element} el base DOM element
   * @param {Object} params
   */
  _cacheElements(el, params) {
    this.el = el;

    if (!this.el) {
      throw new Error('CardSet markup missing');
    } else {
      this.number = new CardNumber(this.el.querySelector('.spark-payment--card-number'), {
        onInput: this._onInput.bind(this),
        onBlur: this._onBlur.bind(this),
        onFocus: this._onFocus.bind(this),
        onCardType: this._onCardType.bind(this),
        cardTypes: params.cardTypes,
        disableCardDefaults: params.disableCardDefaults,
        disableAllCardDefaults: params.disableAllCardDefaults,
        onInvalid: () => {
          onInvalidEvent.input = this.number;
          this.el.dispatchEvent(onInvalidEvent);
        }
      });
      this.expiration = new CardExpiration(this.el.querySelector('.spark-payment--expiration'), {
        onInput: this._onInput.bind(this),
        onBlur: this._onBlur.bind(this),
        onFocus: this._onFocus.bind(this),
        onInvalid: () => {
          onInvalidEvent.input = this.expiration;
          this.el.dispatchEvent(onInvalidEvent);
        }
      });
      this.securityCode = new CardSecurityCode(this.el.querySelector('.spark-payment--card-security-code'), {
        onInput: this._onInput.bind(this),
        onBlur: this._onBlur.bind(this),
        onFocus: this._onFocus.bind(this),
        info: params.info,
        infoToggleAriaLabel: params.infoToggleAriaLabel,
        onInvalid: () => {
          onInvalidEvent.input = this.securityCode;
          this.el.dispatchEvent(onInvalidEvent);
        }
      });
      this.components = [this.number, this.expiration, this.securityCode];
    }
  }


  /**
   * Create bound versions of event listener callbacks and store them.
   * Otherwise we can't unbind from these events later because the
   * function signatures won't match.
   */
  _bindEventListenerCallbacks() {
    this._onInvalidBound = this._onInvalid.bind(this);
    this._onValidBound = this._onValid.bind(this);
  }


  /**
   * Add event listeners for DOM events.
   */
  _addEventListeners() {
    this.el.addEventListener('invalid', this._onInvalidBound, false);
    this.el.addEventListener('valid', this._onValidBound, false);
  }


  /**
   * Remove event listeners for DOM events..
   */
  _removeEventListeners() {
    this.el.removeEventListener('invalid', this._onInvalidBound, false);
    this.el.removeEventListener('valid', this._onValidBound, false);
    this.components.every(component => component._removeEventListeners());
  }


  /**
   * Called when the Card Number field recognizes a new type of card,
   * and used to update the max length of the CVV field
   */
  _onCardType() {
    const type = this.number._cardTypes[this.number.type];
    this.securityCode.maxLength = type.cvv;
    this.onCardType.call(this);
  }


  /**
   * onInput callback for running validation
   */
  _onInput() {
    if (this.valid) {
      this.el.dispatchEvent(onValidEvent);
    }
    this.onInput.call(this);
  }


  /**
   * onBlur callback, for firing provided callback
   */
  _onBlur() {
    this.onBlur.call(this);
  }


  /**
   * onFocus callback, for firing provided callback
   */
  _onFocus() {
    this.onFocus.call(this);
  }


  /**
   * onInvalid method for firing provided callback
   * @param {Event} e
   */
  _onInvalid(e) {
    this.onInvalid.call(this, e);
  }


  /**
   * onValid method for firing provided callback
   */
  _onValid() {
    this.onValid.call(this);
  }


  /**
   * Getter for Primary Component Values
   * @return {Object}
   */
  get value() {
    return {
      number: this.number.value,
      expiration: this.expiration.value,
      securityCode: this.securityCode.value
    };
  }


  /**
   * Setter for Primary Component Values
   * @param {Object} values
   */
  set value(values = {}) {
    this.number.value = values.number || this.number.value;
    this.expiration.value = values.expiration || this.expiration.value;
    this.securityCode.value = values.securityCode || this.securityCode.value;

    if (this.components.some(component => component.value) && this.valid) {
      this.el.dispatchEvent(onValidEvent);
    } else if (this.components.some(component => component.value)) {
      this.el.dispatchEvent(onInvalidEvent);
    }
  }


  /**
   * Getter for Valid state, returns true if component value is valid, false if not
   * @return {Boolean}
   */
  get valid() {
    return this.components.every(component => component.valid);
  }


  /**
   * Default validate function. This can be overridden by passing a
   * custom validate method as a parameter.
   */
  validate() {
    this.onValidate(this.valid, this.value, this);
    return this;
  }


  /**
   * Show the inputs by adding the active state.
   */
  show() {
    this.components.every(component => component.show());
    return this;
  }


  /**
   * Hide the inputs by removing the active state.
   */
  hide() {
    this.components.every(component => component.hide());
    return this;
  }


  /**
   * Returns the current values
   * @return {Object}
   */
  getValue() {
    return this.value;
  }


  /**
   * Set the value of the input.
   * @param {Object} values
   */
  setValue(values = {}) {
    this.value = values;
    return this;
  }


  /**
   * Clear the value of the input.
   */
  clearValue() {
    this.components.every(component => component.value = '');
    return this;
  }


  /**
   * Disable the input.
   */
  disable() {
    this.components.every(component => component.disable());
    return this;
  }


  /**
   * Enable the input.
   */
  enable() {
    this.components.every(component => component.enable());
    return this;
  }


  /**
   * Remove component from DOM.
   * @param {Boolean} leaveElement
   */
  remove(leaveElement) {
    this.components.every(component => component.remove(leaveElement));
    return super.remove(leaveElement);
  }


  /**
   * Update the element in use and the position.
   * @param {Element} el
   * @param {Object} params
   */
  update(el, params = {}) {
    this._removeEventListeners();
    this.components.every(component => component.remove(true));
    this._cacheElements.call(this, el || this.el, params);
    this._addEventListeners();
    return this;
  }

}


/**
 * Default values for internal properties we will be setting.
 * These are set on each construction so we don't leak properties
 * into the prototype chain.
 * @type {Object}
 */
CardSet.prototype.defaults = {
  el: null,
  number: {},
  expiration: {},
  securityCode: {},
  onCardType: () => {},
  onInput: () => {},
  onBlur: () => {},
  onFocus: () => {},
  onValid: () => {},
  onInvalid: () => {},
  onValidate: () => {},
  validate: () => {}
};


/**
 * Whitelisted parameters which can be set on construction.
 * @type {Array}
 */
CardSet.prototype._whitelistedParams = [
  'onCardType',
  'onInput',
  'onBlur',
  'onFocus',
  'onValid',
  'onInvalid',
  'onValidate',
  'validate'
];

export default CardSet;
